import React, { useEffect, useState, useMemo } from "react";
import { auleloApi } from "@/lib/auleloApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plane, Plus, Pencil, Trash2, Loader2, ChevronLeft, MapPin, Calendar, FileText, MessageSquare } from "lucide-react";
import NotesPanel from "@/pages/aulelo/NotesPanel";
import VasaChat from "@/pages/aulelo/VasaChat";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function AuleloTours() {
  const [ministers, setMinisters] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [data, setData] = useState({ tours: [], years: [], grouped: {} });
  const [year, setYear] = useState(null);
  const [month, setMonth] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [addMinisterOpen, setAddMinisterOpen] = useState(false);

  const loadMinisters = async () => {
    try {
      const { data } = await auleloApi.get("/ministers");
      setMinisters(data);
      if (data.length && !selectedId) setSelectedId(data[0].id);
    } catch {/* */}
  };

  const loadTours = async (mid) => {
    if (!mid) return;
    setBusy(true);
    try {
      const { data } = await auleloApi.get(`/ministers/${mid}/tours`);
      setData(data);
      if (!year && data.years?.length) setYear(data.years[0]);
    } catch {
      setData({ tours: [], years: [], grouped: {} });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { loadMinisters(); }, []);
  useEffect(() => {
    if (selectedId) {
      setYear(null); setMonth(null);
      loadTours(selectedId);
    }
  }, [selectedId]);

  const selectedMinister = ministers.find((m) => m.id === selectedId);
  const yearData = year ? (data.grouped[year] || {}) : {};
  const monthsInYear = useMemo(() => {
    return Object.keys(yearData).map(Number).sort((a, b) => a - b);
  }, [yearData]);
  const monthTours = month ? (yearData[month] || []) : [];

  const deleteTour = async (tid) => {
    if (!window.confirm("Delete this visit?")) return;
    try {
      await auleloApi.delete(`/tours/${tid}`);
      toast.success("Deleted");
      loadTours(selectedId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-6" data-testid="aulelo-tours-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-400">// foreign tours</div>
          <h1 className="font-mono text-4xl md:text-5xl font-black tracking-tight mt-2 text-emerald-50">Where they went.</h1>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={selectedId || ""} onValueChange={setSelectedId}>
            <SelectTrigger className="rounded-none bg-black border-emerald-700/40 text-emerald-200 w-72 font-mono uppercase tracking-widest text-[11px]" data-testid="aulelo-minister-select">
              <SelectValue placeholder="Choose minister" />
            </SelectTrigger>
            <SelectContent className="rounded-none bg-black border-emerald-700/40 text-emerald-200">
              {ministers.map((m) => (
                <SelectItem key={m.id} value={m.id} className="rounded-none font-mono text-xs">
                  {m.name} · {m.title || m.department}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => setAddMinisterOpen(true)}
            data-testid="aulelo-add-minister-btn"
            variant="outline"
            className="rounded-none border-emerald-700/40 bg-transparent text-emerald-300 hover:bg-emerald-500/10 font-mono text-[11px] uppercase tracking-widest h-10"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {busy && (<div className="text-center py-12 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline" /></div>)}

      {!busy && selectedMinister && (
        <div className="border border-emerald-900/40">
          {/* Step 1: Year picker */}
          <div className="bg-black/60 border-b border-emerald-900/30 p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">step 1 — year</div>
            <div className="flex flex-wrap gap-2">
              {(data.years || []).map((y) => (
                <button
                  key={y}
                  onClick={() => { setYear(y); setMonth(null); }}
                  data-testid={`aulelo-year-${y}`}
                  className={`px-4 py-2 border font-mono text-sm ${
                    year === y ? "border-emerald-500 bg-emerald-500/15 text-emerald-100"
                               : "border-zinc-800 text-zinc-400 hover:text-emerald-300 hover:border-emerald-800/60"
                  }`}
                >
                  {y}
                </button>
              ))}
              {(data.years || []).length === 0 && (
                <div className="text-zinc-500 font-mono text-sm">No tours logged. Add one below.</div>
              )}
            </div>
          </div>

          {/* Step 2: Month picker */}
          {year && (
            <div className="bg-black/40 border-b border-emerald-900/30 p-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">step 2 — month · {year}</div>
              <div className="grid grid-cols-6 lg:grid-cols-12 gap-1">
                {MONTHS.map((mn, i) => {
                  const num = i + 1;
                  const has = monthsInYear.includes(num);
                  return (
                    <button
                      key={num}
                      onClick={() => has && setMonth(num)}
                      disabled={!has}
                      data-testid={`aulelo-month-${year}-${num}`}
                      className={`px-2 py-3 border font-mono text-xs uppercase tracking-widest text-center ${
                        month === num ? "border-emerald-500 bg-emerald-500/15 text-emerald-100"
                                       : has ? "border-emerald-800/40 text-emerald-300 hover:bg-emerald-500/10"
                                             : "border-zinc-900 text-zinc-700 cursor-not-allowed"
                      }`}
                    >
                      {mn}
                      {has && <div className="text-[9px] text-emerald-500 mt-0.5">{(yearData[num] || []).length}</div>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: Countries + summaries */}
          {year && month && (
            <div className="p-5 space-y-3" data-testid="aulelo-tour-list">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2 flex items-center gap-2">
                <button onClick={() => setMonth(null)} className="hover:text-emerald-300">
                  <ChevronLeft className="w-3 h-3 inline" /> back
                </button>
                · step 3 — visits · {MONTHS[month - 1]} {year}
              </div>
              {monthTours.map((t) => (
                <TourCard key={t.id} t={t} onEdit={() => setEditing(t)} onDelete={() => deleteTour(t.id)} />
              ))}
              <Button
                onClick={() => setEditing({ year, month, kind: "Bilateral" })}
                data-testid="aulelo-add-tour-btn"
                variant="outline"
                className="w-full rounded-none border-dashed border-emerald-800/40 bg-transparent text-emerald-300 hover:bg-emerald-500/10 font-mono uppercase tracking-widest text-xs h-11"
              >
                <Plus className="w-4 h-4 mr-2" /> Add a visit to {MONTHS[month - 1]} {year}
              </Button>
            </div>
          )}
        </div>
      )}

      <TourEditDialog
        editing={editing}
        ministerId={selectedId}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); loadTours(selectedId); }}
      />

      <MinisterAddDialog
        open={addMinisterOpen}
        onClose={() => setAddMinisterOpen(false)}
        onCreated={(m) => { setAddMinisterOpen(false); loadMinisters().then(() => setSelectedId(m.id)); }}
      />
    </div>
  );
}

function TourEditDialog({ editing, ministerId, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (editing) setForm({
      year: editing.year, month: editing.month, country: editing.country || "",
      country_code: editing.country_code || "", kind: editing.kind || "Bilateral",
      summary: editing.summary || "",
    });
  }, [editing]);
  if (!editing) return null;
  const isEdit = !!editing.id;

  const submit = async () => {
    if (!form.country?.trim() || !form.summary?.trim()) {
      toast.error("Country and summary required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) await auleloApi.patch(`/tours/${editing.id}`, form);
      else await auleloApi.post(`/ministers/${ministerId}/tours`, form);
      toast.success(isEdit ? "Updated" : "Added");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="aulelo-tour-dialog" className="rounded-none bg-black border-emerald-700/40 text-emerald-100 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-mono uppercase tracking-widest text-emerald-300">{isEdit ? "Edit visit" : "Add visit"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="font-mono uppercase tracking-widest text-[10px] text-zinc-400">Year</Label>
            <Input type="number" value={form.year || ""} onChange={(e) => setForm({ ...form, year: Number(e.target.value) })} className="mt-1 rounded-none bg-black border-emerald-700/40 text-emerald-100 h-10 font-mono" data-testid="aulelo-tour-year" />
          </div>
          <div>
            <Label className="font-mono uppercase tracking-widest text-[10px] text-zinc-400">Month (1-12)</Label>
            <Input type="number" min={1} max={12} value={form.month || ""} onChange={(e) => setForm({ ...form, month: Number(e.target.value) })} className="mt-1 rounded-none bg-black border-emerald-700/40 text-emerald-100 h-10 font-mono" data-testid="aulelo-tour-month" />
          </div>
          <div className="col-span-2">
            <Label className="font-mono uppercase tracking-widest text-[10px] text-zinc-400">Country</Label>
            <Input value={form.country || ""} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1 rounded-none bg-black border-emerald-700/40 text-emerald-100 h-10 font-mono" data-testid="aulelo-tour-country" />
          </div>
          <div>
            <Label className="font-mono uppercase tracking-widest text-[10px] text-zinc-400">Country code (ISO-2)</Label>
            <Input value={form.country_code || ""} onChange={(e) => setForm({ ...form, country_code: e.target.value.toLowerCase() })} className="mt-1 rounded-none bg-black border-emerald-700/40 text-emerald-100 h-10 font-mono uppercase" maxLength={3} data-testid="aulelo-tour-code" placeholder="e.g. us, gb, fr" />
          </div>
          <div>
            <Label className="font-mono uppercase tracking-widest text-[10px] text-zinc-400">Kind</Label>
            <Select value={form.kind || "Bilateral"} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger className="mt-1 rounded-none bg-black border-emerald-700/40 text-emerald-100 h-10 font-mono"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none bg-black border-emerald-700/40 text-emerald-100">
                <SelectItem value="Bilateral" className="rounded-none font-mono">Bilateral</SelectItem>
                <SelectItem value="Multilateral" className="rounded-none font-mono">Multilateral</SelectItem>
                <SelectItem value="Summit" className="rounded-none font-mono">Summit</SelectItem>
                <SelectItem value="Conference" className="rounded-none font-mono">Conference</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="font-mono uppercase tracking-widest text-[10px] text-zinc-400">Summary</Label>
            <Textarea rows={4} value={form.summary || ""} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="mt-1 rounded-none bg-black border-emerald-700/40 text-emerald-100 font-mono" data-testid="aulelo-tour-summary" />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-none border-emerald-700/40 bg-transparent text-emerald-300 font-mono uppercase tracking-widest text-[11px]" data-testid="aulelo-tour-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-none bg-emerald-500 hover:bg-emerald-400 text-black font-mono uppercase tracking-widest text-[11px]" data-testid="aulelo-tour-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : null}{isEdit ? "Update" : "Add"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MinisterAddDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", title: "", department: "", country_code: "in" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ name: "", title: "", department: "", country_code: "in" }); }, [open]);
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const { data } = await auleloApi.post("/ministers", form);
      toast.success("Added");
      onCreated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="aulelo-minister-dialog" className="rounded-none bg-black border-emerald-700/40 text-emerald-100 max-w-md">
        <DialogHeader><DialogTitle className="font-mono uppercase tracking-widest text-emerald-300">Add minister</DialogTitle></DialogHeader>
        {["name", "title", "department"].map((k) => (
          <div key={k}>
            <Label className="font-mono uppercase tracking-widest text-[10px] text-zinc-400">{k}</Label>
            <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 rounded-none bg-black border-emerald-700/40 text-emerald-100 h-10 font-mono" data-testid={`aulelo-minister-${k}`} />
          </div>
        ))}
        <div>
          <Label className="font-mono uppercase tracking-widest text-[10px] text-zinc-400">Country code</Label>
          <Input value={form.country_code} onChange={(e) => setForm({ ...form, country_code: e.target.value.toLowerCase() })} maxLength={3} className="mt-1 rounded-none bg-black border-emerald-700/40 text-emerald-100 h-10 font-mono" data-testid="aulelo-minister-country" />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-none border-emerald-700/40 bg-transparent text-emerald-300 font-mono uppercase tracking-widest text-[11px]">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-none bg-emerald-500 hover:bg-emerald-400 text-black font-mono uppercase tracking-widest text-[11px]" data-testid="aulelo-minister-save">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Add"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function TourCard({ t, onEdit, onDelete }) {
  const [tab, setTab] = useState("summary");
  return (
    <article className="border border-emerald-900/40 bg-black group" data-testid={`aulelo-tour-${t.id}`}>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            {t.country_code && (
              <img alt={t.country} src={`https://flagcdn.com/40x30/${t.country_code}.png`} className="w-10 h-7 object-cover border border-emerald-900/30" />
            )}
            <div>
              <h3 className="font-mono text-lg font-bold text-emerald-100">{t.country}</h3>
              <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-500/80 mt-0.5">
                <Calendar className="w-3 h-3 inline mr-1" /> {MONTHS[t.month - 1]} {t.year} · {t.kind}
              </div>
            </div>
          </div>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
            <button onClick={onEdit} data-testid={`aulelo-tour-edit-${t.id}`} className="p-2 border border-zinc-800 text-zinc-500 hover:text-emerald-300 hover:border-emerald-700/40"><Pencil className="w-3 h-3" /></button>
            <button onClick={onDelete} data-testid={`aulelo-tour-delete-${t.id}`} className="p-2 border border-zinc-800 text-zinc-500 hover:text-red-400 hover:border-red-700/40"><Trash2 className="w-3 h-3" /></button>
          </div>
        </div>
      </div>
      <div className="border-t border-emerald-900/30 flex">
        {[
          { id: "summary", label: "Summary", icon: FileText },
          { id: "notes",   label: "My notes", icon: MessageSquare },
          { id: "vasa",    label: "Ask Vasa", icon: MessageSquare },
        ].map((tt) => (
          <button key={tt.id} onClick={() => setTab(tt.id)} data-testid={`tour-tab-${t.id}-${tt.id}`}
            className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 border-r border-emerald-900/30 flex items-center gap-2 ${
              tab === tt.id ? "bg-emerald-500/10 text-emerald-200" : "text-zinc-500 hover:text-emerald-300"
            }`}>
            <tt.icon className="w-3 h-3" /> {tt.label}
          </button>
        ))}
      </div>
      <div className="p-5">
        {tab === "summary" && (
          <p className="text-zinc-300 leading-relaxed" data-testid={`tour-summary-${t.id}`}>{t.summary}</p>
        )}
        {tab === "notes" && (
          <NotesPanel tourId={t.id} scopeLabel={`Notes — ${t.country} · ${MONTHS[t.month - 1]} ${t.year}`} />
        )}
        {tab === "vasa" && (
          <VasaChat tourId={t.id} compact />
        )}
      </div>
    </article>
  );
}
